/**
 * Beacon Notification Service API client
 *
 * Provides authenticated HTTP calls to the Beacon notification backend.
 * Base URL is resolved per hosting environment:
 *   - Development (local): `/beacon-api` (Vite proxy → beacon.ogintegration.us)
 *   - Integration:          `https://beacon.ogintegration.us`
 *   - Staging:              `https://beacon.ogstaging.us`
 *   - Production:           `https://beacon.opengov.com`
 */

import { HostingEnvironment } from '@opengov/psp-contract';
import { detectPspHostingEnvironment } from './utils';
import type {
  NotificationsApiResponse,
  FetchNotificationsParams,
  FetchUnreadCountParams,
  UnreadCountResponse,
} from '../pages/Notifications/types';

/**
 * Resolve the Beacon API base URL for the current hosting environment.
 * In development mode the Vite dev-server proxy handles the request,
 * so we return a path-only prefix (`/beacon-api`).
 */
export function getBeaconBaseUrl(): string {
  const env = detectPspHostingEnvironment();
  switch (env) {
    case HostingEnvironment.DEVELOPMENT:
      return '/beacon-api';
    case HostingEnvironment.INTEGRATION:
      return 'https://beacon.ogintegration.us';
    case HostingEnvironment.STAGING:
      return 'https://beacon.ogstaging.us';
    case HostingEnvironment.PRODUCTION:
      return 'https://beacon.opengov.com';
    default:
      return '/beacon-api';
  }
}

/**
 * Thin wrapper around `fetch` that adds Bearer auth and standard headers.
 * Mirrors the existing `authFetch` pattern used elsewhere in the PSP.
 */
async function beaconFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  const baseUrl = getBeaconBaseUrl();
  const url = `${baseUrl}${path}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `Beacon API error ${response.status}: ${response.statusText}${errorBody ? ` — ${errorBody}` : ''}`
    );
  }

  // Some endpoints (POST read/unread) may return 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Request body for POST /api/v1/notifications
 *
 * **Context is a discriminated union** — use exactly one mode:
 *
 * - **Payload mode** (`context.payloads`): delivers in-app only. `audience.urn` required.
 * - **Template mode** (`context.templateId` + `context.templateData`): delivers in-app + email.
 *   Required when using `audience.emailAddress`. Cannot combine with `payloads`.
 *
 * **Audience** is mutually exclusive:
 * - `urn`: platform/public user or group URNs — works with both context modes
 * - `emailAddress`: external email addresses — requires template mode
 *
 * Source: `GET https://beacon.ogintegration.us/api/v1/spec` (verified 2026-08-11)
 */

/** Payload-based context: in-app only, no email channel */
export interface BeaconPayloadContext {
  payloads: {
    inapp: {
      title: string; // maxLength 500
      body: string; // maxLength 2000
      deepLinkUrl?: string; // maxLength 1000
    };
  };
  channelOptions?: BeaconChannelOptions;
  templateId?: never;
  templateData?: never;
}

/** Template-based context: in-app + email channels */
export interface BeaconTemplateContext {
  templateId: string; // e.g. "default", "po_received", "monthly_digest"
  templateData: Record<string, string>;
  templateProductOverride?: string;
  channelOptions?: BeaconChannelOptions;
  payloads?: never;
}

interface BeaconChannelOptions {
  email?: {
    attachments?: Array<{
      filename: string;
      contentType: string;
      url: string; // pre-signed HTTPS URL, maxLength 4096
    }>;
  };
}

export interface CreateNotificationRequest {
  idempotencyKey: string; // maxLength 255
  product: string; // e.g. "vm", "budget", "procurement", or a full URN — maxLength 100
  type: 'informational' | 'action_required' | 'mentions';
  priority: 'low' | 'normal' | 'urgent';
  /**
   * Mutually exclusive: provide `urn` OR `emailAddress`, not both.
   * `emailAddress` requires template-based context.
   */
  audience:
    | { urn: string[]; emailAddress?: never } // minItems(1); pattern: urn:opengov:(platform:(user|group)|public:user)/{uuid}
    | { emailAddress: string[]; urn?: never }; // minItems(1); template mode only
  context: BeaconPayloadContext | BeaconTemplateContext;
  taskId?: string; // maxLength 255
  subject?: string; // resource URN, e.g. "urn:opengov:procurement:purchase-order/12345" — maxLength 255
  brandingOverride?: {
    fromEmail?: string;
    fromName?: string; // maxLength 100
    replyToEmail?: string;
    replyToName?: string; // maxLength 100
    ccEmails?: string[];
    bccEmails?: string[];
  };
}

// ---------------------------------------------------------------------------
// Public API functions
// ---------------------------------------------------------------------------

/**
 * Fetch paginated notifications for an entity.
 * GET /api/v1/notifications?entityId=…
 *
 * Currently supported filters: read, starred, taskId
 * TODO: When backend adds support, uncomment lines below for product/priority/search filters
 */
export async function fetchBeaconNotifications(
  token: string,
  entityId: string,
  params?: FetchNotificationsParams,
  signal?: AbortSignal
): Promise<NotificationsApiResponse> {
  const query = new URLSearchParams({ entityId });

  if (params?.limit) query.append('limit', String(params.limit));
  if (params?.cursor) query.append('cursor', params.cursor);
  if (params?.read !== undefined) query.append('read', params.read);
  if (params?.starred !== undefined) query.append('starred', params.starred);
  if (params?.taskId) query.append('taskId', params.taskId);
  if (params?.actionable) query.append('actionable', params.actionable);
  if (params?.subject) query.append('subject', params.subject);

  return beaconFetch<NotificationsApiResponse>(`/api/v1/notifications?${query}`, token, { signal });
}

/**
 * Fetch the notification count for an entity.
 * GET /api/v1/notifications/count?entityId=…
 *
 * Defaults to unread-only (read=false). Supports optional actionable/subject filtering.
 */
export async function fetchBeaconUnreadCount(
  token: string,
  entityId: string,
  params?: FetchUnreadCountParams,
  signal?: AbortSignal
): Promise<UnreadCountResponse> {
  const query = new URLSearchParams({ entityId, read: params?.read ?? 'false' });
  if (params?.actionable) query.append('actionable', params.actionable);
  if (params?.subject) query.append('subject', params.subject);

  return beaconFetch<UnreadCountResponse>(`/api/v1/notifications/count?${query}`, token, {
    signal,
  });
}

/**
 * Mark a single notification as read.
 * POST /api/v1/notifications/{id}/read?entityId=…
 */
export async function markBeaconNotificationRead(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  const query = new URLSearchParams({ entityId });

  return beaconFetch<void>(`/api/v1/notifications/${notificationId}/read?${query}`, token, {
    method: 'POST',
  });
}

/**
 * Mark a single notification as unread.
 * POST /api/v1/notifications/{id}/unread?entityId=…
 */
export async function markBeaconNotificationUnread(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  const query = new URLSearchParams({ entityId });

  return beaconFetch<void>(`/api/v1/notifications/${notificationId}/unread?${query}`, token, {
    method: 'POST',
  });
}

/**
 * Star a single notification (save for later).
 * POST /api/v1/notifications/{id}/star?entityId=…
 */
export async function starBeaconNotification(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  const query = new URLSearchParams({ entityId });

  return beaconFetch<void>(`/api/v1/notifications/${notificationId}/star?${query}`, token, {
    method: 'POST',
  });
}

/**
 * Unstar a single notification (remove from saved).
 * POST /api/v1/notifications/{id}/unstar?entityId=…
 */
export async function unstarBeaconNotification(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  const query = new URLSearchParams({ entityId });

  return beaconFetch<void>(`/api/v1/notifications/${notificationId}/unstar?${query}`, token, {
    method: 'POST',
  });
}

/**
 * Fetch notifications for an entity grouped by a digest window.
 * GET /api/v1/notifications/digest/{digestId}?entityId=…
 *
 * Returns the same NotificationsApiResponse shape as the regular feed.
 * The response always has hasMore: false (not paginated).
 * Beacon enforces that digestId belongs to the authenticated user;
 * mismatched or stale IDs return an empty list (not 403).
 */
export async function fetchBeaconDigestNotifications(
  token: string,
  digestId: string,
  entityId: string,
  signal?: AbortSignal
): Promise<NotificationsApiResponse> {
  const query = new URLSearchParams({ entityId });
  return beaconFetch<NotificationsApiResponse>(
    `/api/v1/notifications/digest/${encodeURIComponent(digestId)}?${query}`,
    token,
    { signal }
  );
}

/**
 * Create a new notification via the Beacon API.
 * POST /api/v1/notifications?entityId=…
 *
 * Returns the raw Response so callers can inspect status codes (e.g. 202 Accepted).
 */
export async function createBeaconNotification(
  token: string,
  entityId: string,
  request: CreateNotificationRequest
): Promise<Response> {
  const baseUrl = getBeaconBaseUrl();
  const query = new URLSearchParams({ entityId });
  const url = `${baseUrl}/api/v1/notifications?${query}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `Beacon API error ${response.status}: ${response.statusText}${errorBody ? ` — ${errorBody}` : ''}`
    );
  }

  return response;
}
